import {
  Bell,
  CalendarDays,
  CheckCheck,
  MessageSquareText,
  ShoppingBag,
  Star,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  loadNovaCmsNotifications,
  markAllNovaCmsNotificationsRead,
  markNovaCmsNotificationRead,
  type NovaCmsNotification,
} from "./nova-cms-notifications";

function NotificationIcon({
  type,
}: {
  type: NovaCmsNotification["notification_type"];
}) {
  if (type === "reservation") {
    return <CalendarDays size={15} />;
  }

  if (
    type === "service_enquiry" ||
    type === "website_enquiry"
  ) {
    return <MessageSquareText size={15} />;
  }

  if (type === "feedback") {
    return <Star size={15} />;
  }

  if (type === "order") {
    return <ShoppingBag size={15} />;
  }

  return <Bell size={15} />;
}

function formatNotificationTime(
  value: string,
) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(
    "en-KE",
    {
      dateStyle: "medium",
      timeStyle: "short",
    },
  ).format(date);
}

export function NovaCmsNotificationBell() {
  const [notifications, setNotifications] =
    useState<NovaCmsNotification[]>([]);
  const [open, setOpen] =
    useState(false);
  const [busy, setBusy] =
    useState(false);

  const containerRef =
    useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(
    async () => {
      const next =
        await loadNovaCmsNotifications();

      setNotifications(next);
    },
    [],
  );

  useEffect(() => {
    void refresh();

    const interval =
      window.setInterval(
        () => {
          void refresh();
        },
        30000,
      );

    const handleFocus = () => {
      void refresh();
    };

    window.addEventListener(
      "focus",
      handleFocus,
    );

    return () => {
      window.clearInterval(interval);

      window.removeEventListener(
        "focus",
        handleFocus,
      );
    };
  }, [refresh]);

  useEffect(() => {
    function handlePointerDown(
      event: MouseEvent,
    ) {
      const target =
        event.target as Node | null;

      if (
        !target ||
        containerRef.current?.contains(
          target,
        )
      ) {
        return;
      }

      setOpen(false);
    }

    document.addEventListener(
      "mousedown",
      handlePointerDown,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handlePointerDown,
      );
    };
  }, []);

  const unreadCount =
    useMemo(
      () =>
        notifications.filter(
          (notification) =>
            !notification.is_read,
        ).length,
      [notifications],
    );

  async function handleOpenNotification(
    notification: NovaCmsNotification,
  ) {
    if (!notification.is_read) {
      const changed =
        await markNovaCmsNotificationRead(
          notification.id,
        );

      if (changed) {
        setNotifications(
          (current) =>
            current.map((item) =>
              item.id === notification.id
                ? {
                    ...item,
                    is_read: true,
                  }
                : item,
            ),
        );
      }
    }

    setOpen(false);

    if (notification.destination) {
      window.location.href =
        notification.destination;
    }
  }

  async function handleMarkAllRead() {
    setBusy(true);

    const changed =
      await markAllNovaCmsNotificationsRead();

    if (changed) {
      setNotifications(
        (current) =>
          current.map((item) => ({
            ...item,
            is_read: true,
          })),
      );
    }

    setBusy(false);
  }

  return (
    <div
      ref={containerRef}
      className="relative"
    >
      <button
        type="button"
        onClick={() =>
          setOpen((current) => !current)
        }
        className="relative flex h-10 w-10 items-center justify-center border border-[#f3ead7]/20 text-[#f3ead7] transition-colors hover:border-[#d6bd91] hover:text-[#d6bd91]"
        aria-label="Nova HQ notifications"
        aria-expanded={open}
      >
        <Bell
          size={16}
          strokeWidth={1.5}
        />

        {unreadCount > 0 && (
          <span className="absolute -right-2 -top-2 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-[#9d725c] px-1 text-[9px] font-bold text-white">
            {unreadCount > 99
              ? "99+"
              : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-50 w-[min(390px,calc(100vw-2rem))] border border-[#b5bbae] bg-[#eee9dc] text-[#314a3b] shadow-2xl">
          <div className="flex items-center justify-between border-b border-[#b5bbae] px-5 py-4">
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                Nova HQ
              </p>

              <p className="mt-1 font-['Cormorant_Garamond'] text-2xl">
                Notifications
              </p>
            </div>

            {unreadCount > 0 && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void handleMarkAllRead()
                }
                className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.12em] text-[#718076] hover:text-[#9d725c] disabled:opacity-50"
              >
                <CheckCheck size={13} />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[430px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <Bell
                  className="mx-auto text-[#9d725c]"
                  size={22}
                  strokeWidth={1.2}
                />

                <p className="mt-4 font-['Cormorant_Garamond'] text-2xl">
                  All quiet for now.
                </p>

                <p className="mx-auto mt-2 max-w-xs text-xs leading-5 text-[#718076]">
                  New reservations,
                  enquiries, feedback and
                  other guest activity will
                  appear here.
                </p>
              </div>
            ) : (
              notifications.map(
                (notification) => (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() =>
                      void handleOpenNotification(
                        notification,
                      )
                    }
                    className={[
                      "flex w-full gap-4 border-b border-[#b5bbae]/70 px-5 py-4 text-left transition-colors hover:bg-white/35",
                      notification.is_read
                        ? "bg-transparent"
                        : "bg-[#d6bd91]/12",
                    ].join(" ")}
                  >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center border border-[#9d725c]/35 text-[#9d725c]">
                      <NotificationIcon
                        type={
                          notification.notification_type
                        }
                      />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-start justify-between gap-3">
                        <span className="text-sm font-medium">
                          {
                            notification.title
                          }
                        </span>

                        {!notification.is_read && (
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#9d725c]" />
                        )}
                      </span>

                      <span className="mt-1 block text-xs leading-5 text-[#667067]">
                        {
                          notification.message
                        }
                      </span>

                      <span className="mt-2 block text-[8px] font-semibold uppercase tracking-[0.12em] text-[#8a928b]">
                        {formatNotificationTime(
                          notification.created_at,
                        )}
                      </span>
                    </span>
                  </button>
                ),
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
